import { useCallback, useEffect, useState } from 'react';
import { Navigate, NavLink, Outlet, Route, Routes, useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import {
  AlertTriangle, BookOpenCheck, Building2, CalendarDays, ChevronRight, CircleDollarSign, ExternalLink, FileBarChart,
  FileClock, Gauge, Gift, Globe2, KeyRound, Landmark, LogOut, MapPinned, Package, Sparkles, Star,
  Menu, Moon, Pencil, Plus, RefreshCw, Save, Settings, ShieldCheck, Store, Sun, Trash2, Users, X,
} from 'lucide-react';
import { api, session } from './api.js';
import { API_URL } from './api.js';
import { io } from 'socket.io-client';
import { AccessPage, AuditPage, BookingPage, SchedulePage, CashShiftsPage, CustomersPage, InventoryPage, OperationsDashboard, PointLedgerPage, PointRulesPage, RedemptionsPage, ReportsPage, RewardsPage } from './Operations.jsx';

// booking.oneplayplatform.com: trang Booking riêng (mở tab riêng ở quầy) — chỉ nội dung Booking, không có thanh menu.
// schedule.oneplayplatform.com: trang Lịch làm riêng (xếp ca, chốt lịch) — tương tự.
const HOSTNAME = typeof window !== 'undefined' ? window.location.hostname : '';
const BOOKING_HOST = HOSTNAME.startsWith('booking.');
const SCHEDULE_HOST = HOSTNAME.startsWith('schedule.');
const MAIN_HOST = !BOOKING_HOST && !SCHEDULE_HOST && HOSTNAME.endsWith('oneplayplatform.com');
const BOOKING_URL = MAIN_HOST ? 'https://booking.oneplayplatform.com' : null;
const SCHEDULE_URL = MAIN_HOST ? 'https://schedule.oneplayplatform.com' : null;
// menu ở trang quản trị mở trang riêng ở tab mới (kèm chuyển phiên đăng nhập)
const SUBDOMAIN_PAGES = { '/app/booking': BOOKING_URL, '/app/schedule': SCHEDULE_URL };

const money = new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 });
const date = new Intl.DateTimeFormat('vi-VN', { dateStyle: 'short' });
const dateTime = new Intl.DateTimeFormat('vi-VN', { dateStyle: 'short', timeStyle: 'short' });
const macroRegionOptions = [
  { id: 'BAC', name: 'Bắc' },
  { id: 'TRUNG', name: 'Trung' },
  { id: 'NAM', name: 'Nam' },
];

function Logo({ compact = false }) {
  return <div className={`brand ${compact ? 'compact' : ''}`}>
    <img src="/assets/logo-oneplay.png" alt="OnePlay" />
    <div><strong>OnePlay</strong><span>MANAGERS</span></div>
  </div>;
}

function Notice({ notice, onClose }) {
  // Thông báo thành công tự ẩn; lỗi giữ lại đến khi người dùng đóng.
  useEffect(() => {
    if (notice?.type !== 'success') return undefined;
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [notice]);
  if (!notice) return null;
  return <div className={`notice ${notice.type || 'info'}`}><span>{notice.message}</span><button onClick={onClose}><X size={16} /></button></div>;
}

function ThemeToggle({ theme, onChange, compact = false }) {
  const next = theme === 'dark' ? 'light' : 'dark';
  return <button className={`theme-toggle ${compact ? 'compact' : ''}`} type="button" onClick={() => onChange(next)} title={`Chuyển sang giao diện ${next === 'light' ? 'sáng' : 'tối'}`} aria-label={`Giao diện hiện tại: ${theme === 'light' ? 'sáng' : 'tối'}. Bấm để chuyển.`}>
    {theme === 'light' ? <Sun size={17} /> : <Moon size={17} />}
    <span>{theme === 'light' ? 'Sáng' : 'Tối'}</span>
  </button>;
}

const LOGIN_TEXT = {
  booking: ['ONEPLAY BOOKING', 'Đăng nhập Booking', 'Dùng tài khoản OnePlay Managers để xem và đặt lịch máy của cơ sở.'],
  schedule: ['ONEPLAY · LỊCH LÀM', 'Đăng nhập Lịch làm', 'Dùng tài khoản OnePlay Managers để xếp ca và chốt lịch làm của cơ sở.'],
  main: ['ONEPLAY MANAGERS', 'Đăng nhập quản trị', 'Sử dụng tài khoản được OnePlay HQ cấp.'],
};

function LoginPage({ onLogin, notice, setNotice, theme, setTheme, variant = 'main' }) {
  const [loginEyebrow, loginTitle, loginHint] = LOGIN_TEXT[variant] || LOGIN_TEXT.main;
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const submit = async (event) => {
    event.preventDefault(); setLoading(true); setNotice(null);
    try { await api.login(form.email, form.password); await onLogin(); }
    catch (error) { setNotice({ type: 'error', message: error.message }); }
    finally { setLoading(false); }
  };
  return <main className="login-shell">
    <div className="login-theme"><ThemeToggle theme={theme} onChange={setTheme} /></div>
    <div className="login-aurora one" /><div className="login-aurora two" />
    <section className="login-story">
      <Logo />
      <div className="hero-chip"><ShieldCheck size={15} /> Trung tâm quản trị OnePlay</div>
      <h1>Một hệ thống.<br /><span>Toàn bộ chuỗi.</span></h1>
      <p>Điều hành booking, khách hàng thành viên, đổi quà, tồn kho và tiền mặt theo từng cơ sở trên cùng một nền tảng.</p>
      <div className="hero-metrics"><div><strong>Tập trung</strong><span>Một nguồn dữ liệu quản trị</span></div><div><strong>Kiểm soát</strong><span>Phân quyền và nhật ký đầy đủ</span></div></div>
    </section>
    <section className="login-card">
      <div className="mobile-logo"><Logo /></div>
      <p className="eyebrow">{loginEyebrow}</p><h2>{loginTitle}</h2><p>{loginHint}</p>
      <Notice notice={notice} onClose={() => setNotice(null)} />
      <form onSubmit={submit}>
        <label>Email<input autoComplete="username" type="email" required value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} placeholder="admin@oneplay.vn" /></label>
        <label>Mật khẩu<input autoComplete="current-password" type="password" required value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} placeholder="••••••••••••" /></label>
        <button className="btn primary wide" disabled={loading}>{loading ? <RefreshCw className="spin" size={18} /> : <ShieldCheck size={18} />} Đăng nhập</button>
      </form>
      <small>Mọi lần đăng nhập và thay đổi quản trị đều được ghi nhận.</small>
    </section>
  </main>;
}

const navGroups = [
  { title: 'Vận hành', items: [
    ['/app/dashboard', Gauge, 'Tổng quan'],
    ['/app/booking', BookOpenCheck, 'Booking'],
    ['/app/schedule', CalendarDays, 'Lịch làm'],
    ['/app/customers', Users, 'Khách hàng'],
    ['/app/points', Star, 'Điểm thành viên'],
    ['/app/point-rules', Sparkles, 'Điểm thưởng & sự kiện'],
    ['/app/rewards', Gift, 'Danh mục quà'],
    ['/app/inventory', Package, 'Tồn kho quà'],
    ['/app/redemptions', Gift, 'Đổi quà'],
    ['/app/cash-shifts', CircleDollarSign, 'Bảng giá & cấu hình ca'],
    ['/app/reports', FileBarChart, 'Báo cáo'],
  ] },
  { title: 'Tổ chức', items: [
    ['/app/regions', MapPinned, 'Vùng hoạt động'],
    ['/app/partners', Building2, 'Đối tác nhượng quyền'],
    ['/app/stores', Store, 'Cơ sở'],
  ] },
  { title: 'Quản trị', items: [
    ['/app/users', Users, 'Người dùng'],
    ['/app/access', KeyRound, 'Phân quyền'],
  ] },
  { title: 'Hệ thống', items: [
    ['/app/audit', FileClock, 'Nhật ký hoạt động'],
    ['/app/settings', Settings, 'Cấu hình chung'],
  ] },
];

/** Socket.IO của Managers: phát sự kiện 'oneplay:realtime' cho các trang tự tải lại; trả về trạng thái kết nối. */
function useRealtime() {
  const [live, setLive] = useState(false);
  useEffect(() => {
    const notify = (eventName, payload) => window.dispatchEvent(new CustomEvent('oneplay:realtime', { detail: { eventName, payload } }));
    // Token được đọc lại ở mỗi lần kết nối để dùng access token mới sau khi làm mới phiên.
    const socket = io(API_URL.replace(/\/api\/v1$/, ''), { auth: (callback) => callback({ token: session.accessToken }) });
    let missedEvents = false;
    socket.onAny(notify);
    socket.on('connect', () => {
      setLive(true);
      // Trong lúc mất kết nối có thể đã lỡ sự kiện: tải lại toàn bộ dữ liệu đang hiển thị từ API.
      if (missedEvents) notify('sync', null);
      missedEvents = false;
    });
    socket.on('disconnect', () => { setLive(false); missedEvents = true; });
    socket.on('connect_error', async (error) => {
      setLive(false); missedEvents = true;
      // Server từ chối vì access token hết hạn thì Socket.IO không tự thử lại: làm mới phiên rồi kết nối lại.
      if (error.message === 'Unauthorized' && await api.refresh()) socket.connect();
    });
    return () => socket.disconnect();
  }, []);
  return live;
}

let handoffStarted = false;

/** Mở trang riêng (booking. / schedule.) ở tab mới và chuyển phiên đăng nhập sang (không phải đăng nhập lại). */
async function openSubdomainTab(event, url) {
  event.preventDefault();
  // mở tab ngay trong cú bấm để không bị trình duyệt chặn popup, rồi mới lấy mã chuyển phiên
  const tab = window.open('', '_blank');
  try {
    const { code } = await api.post('/auth/handoff', {});
    if (tab) { tab.opener = null; tab.location.href = `${url}/#handoff=${encodeURIComponent(code)}`; }
    else window.location.href = `${url}/#handoff=${encodeURIComponent(code)}`;
  } catch {
    if (tab) tab.location.href = url;
  }
}

// Bảng phân quyền xem OnePlay Manager: menu vai trò không được xem (dù có quyền dữ liệu để các trang khác dùng chung).
const TIER_HIDDEN = {
  franchise_owner: ['/app/audit'],
  store_manager: ['/app/dashboard', '/app/customers', '/app/point-rules', '/app/rewards', '/app/reports', '/app/stores', '/app/users', '/app/audit'],
};

function AppLayout({ auth, onLogout, notice, setNotice, theme, setTheme }) {
  const [mobileNav, setMobileNav] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const permissions = auth.user.permissions || [];
  const allowed = (path) => {
    if (auth.user.role_key === 'super_admin') return true;
    if ((TIER_HIDDEN[auth.user.role_key] || []).includes(path)) return false;
    const checks = {
      '/app/dashboard': ['branches.view'], '/app/booking': ['bookings.view'], '/app/schedule': ['schedule.arrange'], '/app/customers': ['customers.view'], '/app/points': ['customers.view'], '/app/point-rules': ['points.configure', 'customers.view'],
      '/app/rewards': ['rewards.view'], '/app/inventory': ['reward_inventory.view'], '/app/redemptions': ['redemptions.view'],
      '/app/cash-shifts': ['cash_shifts.configure'], '/app/reports': ['reports.branch', 'reports.franchise', 'reports.region', 'reports.system'],
      '/app/stores': ['branches.view'], '/app/users': ['users.view'], '/app/audit': ['audit_logs.view'], '/app/access': ['roles.manage'],
    };
    // Vùng hoạt động chỉ để HQ phân nhóm cơ sở
    if (path === '/app/regions') return false;
    if (path === '/app/partners') return ['region_manager', 'franchise_owner'].includes(auth.user.role_key);
    if (path === '/app/settings') return false;
    return (checks[path] || []).some((key) => permissions.includes(key));
  };
  const visibleGroups = navGroups.map((group) => ({ ...group, items: group.items.filter(([path]) => allowed(path)) })).filter((group) => group.items.length);
  const current = visibleGroups.flatMap((group) => group.items).find(([path]) => location.pathname === path);
  useEffect(() => setMobileNav(false), [location.pathname]);
  // Gõ thẳng đường dẫn trang không được xem (hoặc mặc định Tổng quan) thì chuyển về trang đầu tiên được xem
  const home = visibleGroups[0]?.items[0]?.[0];
  const blocked = navGroups.some((group) => group.items.some(([path]) => path === location.pathname)) && !allowed(location.pathname);
  useEffect(() => { if (blocked && home) navigate(home, { replace: true }); }, [blocked, home, navigate]);
  const live = useRealtime();
  return <div className="app-shell">
    <aside className={mobileNav ? 'sidebar open' : 'sidebar'}>
      <div className="sidebar-top"><Logo compact /><button className="icon-btn close-nav" onClick={() => setMobileNav(false)}><X /></button></div>
      <nav>{visibleGroups.map((group) => <div className="nav-group" key={group.title}><p>{group.title}</p>{group.items.map(([path, Icon, label]) => SUBDOMAIN_PAGES[path] ? <a key={path} href={SUBDOMAIN_PAGES[path]} target="_blank" rel="noreferrer" onClick={(event) => openSubdomainTab(event, SUBDOMAIN_PAGES[path])}><Icon size={18} /><span>{label}</span><ExternalLink className="nav-arrow" size={15} /></a> : <NavLink key={path} to={path} className={({ isActive }) => isActive ? 'active' : ''}><Icon size={18} /><span>{label}</span><ChevronRight className="nav-arrow" size={15} /></NavLink>)}</div>)}</nav>
      <div className="sidebar-user"><div className="avatar">{auth.user.full_name?.slice(0, 1)}</div><div><strong>{auth.user.full_name}</strong><span>{auth.user.role_key}</span></div><button className="icon-btn" onClick={onLogout} title="Đăng xuất"><LogOut size={17} /></button></div>
    </aside>
    <section className="workspace">
      <header className="topbar">
        <div className="topbar-title"><button className="icon-btn menu-btn" onClick={() => setMobileNav(true)}><Menu /></button><div><small>ONEPLAY MANAGERS</small><strong>{current?.[2] || 'Quản trị'}</strong></div></div>
        <div className="topbar-actions"><ThemeToggle theme={theme} onChange={setTheme} compact /><span className={live ? 'live-dot' : 'live-dot offline'}>{live ? 'TRỰC TUYẾN' : 'MẤT KẾT NỐI · ĐANG THỬ LẠI'}</span></div>
      </header>
      <Notice notice={notice} onClose={() => setNotice(null)} />
      <Outlet context={{ auth, setNotice }} />
    </section>
  </div>;
}

/** booking.oneplayplatform.com: chỉ trang Booking, thanh trên tối giản (không menu). */
function BookingLayout({ auth, onLogout, notice, setNotice, theme, setTheme }) {
  const live = useRealtime();
  const canView = auth.user.role_key === 'super_admin' || (auth.user.permissions || []).includes('bookings.view');
  const canRedeem = auth.user.role_key === 'super_admin' || (auth.user.permissions || []).includes('redemptions.view');
  return <div className="booking-shell">
    <header className="topbar">
      <div className="topbar-title"><Logo compact /><div><small>ONEPLAY</small><strong>Booking</strong></div></div>
      <nav className="booking-tabs">
        <NavLink to="/" end className={({ isActive }) => isActive ? 'active' : ''}><BookOpenCheck size={16} /> Booking</NavLink>
        {canRedeem && <NavLink to="/doi-qua" className={({ isActive }) => isActive ? 'active' : ''}><Gift size={16} /> Đổi quà</NavLink>}
      </nav>
      <div className="topbar-actions">
        <ThemeToggle theme={theme} onChange={setTheme} compact />
        <span className={live ? 'live-dot' : 'live-dot offline'}>{live ? 'TRỰC TUYẾN' : 'MẤT KẾT NỐI · ĐANG THỬ LẠI'}</span>
        <span className="booking-user">{auth.user.full_name}</span>
        <button className="icon-btn" onClick={onLogout} title="Đăng xuất"><LogOut size={17} /></button>
      </div>
    </header>
    <Notice notice={notice} onClose={() => setNotice(null)} />
    {canView ? <Outlet context={{ auth, setNotice }} /> : <main className="content-page"><div className="empty-state"><div><KeyRound /></div><h3>Tài khoản chưa có quyền Booking</h3><p>Liên hệ quản lý để được cấp quyền xem booking.</p></div></main>}
  </div>;
}

/** schedule.oneplayplatform.com: chỉ trang Lịch làm (xếp ca, chốt lịch, duyệt đổi ca), thanh trên tối giản. */
function ScheduleLayout({ auth, onLogout, notice, setNotice, theme, setTheme }) {
  const live = useRealtime();
  const canView = auth.user.role_key === 'super_admin' || (auth.user.permissions || []).includes('schedule.arrange');
  return <div className="booking-shell">
    <header className="topbar">
      <div className="topbar-title"><Logo compact /><div><small>ONEPLAY</small><strong>Lịch làm</strong></div></div>
      <div className="topbar-actions">
        <ThemeToggle theme={theme} onChange={setTheme} compact />
        <span className={live ? 'live-dot' : 'live-dot offline'}>{live ? 'TRỰC TUYẾN' : 'MẤT KẾT NỐI · ĐANG THỬ LẠI'}</span>
        <span className="booking-user">{auth.user.full_name}</span>
        <button className="icon-btn" onClick={onLogout} title="Đăng xuất"><LogOut size={17} /></button>
      </div>
    </header>
    <Notice notice={notice} onClose={() => setNotice(null)} />
    {canView ? <Outlet context={{ auth, setNotice }} /> : <main className="content-page"><div className="empty-state"><div><KeyRound /></div><h3>Tài khoản chưa có quyền xếp lịch</h3><p>Nhân viên pick ca, xem lịch và xin đổi ca trong OnePlay Station → Lịch làm.</p></div></main>}
  </div>;
}

function PageHeader({ eyebrow, title, description, actions }) {
  return <header className="page-header"><div><p className="eyebrow">{eyebrow}</p><h1>{title}</h1>{description && <p>{description}</p>}</div>{actions && <div className="page-actions">{actions}</div>}</header>;
}

const regionColumns = { code: 'Mã vùng', name: 'Tỉnh / thành phố', status: 'Trạng thái', store_count: 'Số cửa hàng', created_at: 'Ngày tạo' };
const partnerColumns = { name: 'Đối tác', legal_name: 'Pháp nhân', tax_code: 'Mã số thuế', representative_name: 'Đại diện', email: 'Email đăng nhập', phone: 'Điện thoại', store_count: 'Cửa hàng', status: 'Trạng thái' };
const storeColumns = { code: 'Mã', name: 'Cửa hàng', partner_name: 'Đối tác', region_name: 'Vùng', address: 'Địa chỉ cơ sở', status: 'Trạng thái' };
const userColumns = { full_name: 'Họ tên', email: 'Email', role_name: 'Vai trò', partner_name: 'Chủ cơ sở', store_names: 'Cơ sở làm việc', status: 'Trạng thái', last_login_at: 'Đăng nhập gần nhất' };

const resources = {
  regions: { eyebrow: 'ORGANIZATION', title: 'Vùng hoạt động', singular: 'vùng hoạt động', description: 'Phân loại Bắc, Trung, Nam và chọn tỉnh/thành phố từ dữ liệu hành chính Việt Nam', endpoint: '/admin/regions', columns: regionColumns, createLabel: 'Thêm vùng', fields: [
    { key: 'code', label: 'Mã vùng', type: 'select', required: true, options: macroRegionOptions },
    { key: 'province_code', label: 'Tỉnh / thành phố', type: 'select', endpoint: '/admin/provinces', required: true, number: true },
    { key: 'status', label: 'Trạng thái', type: 'select', editOnly: true, required: true, options: [{ id: 'active', name: 'Hoạt động' }, { id: 'archived', name: 'Lưu trữ' }] },
  ] },
  partners: { eyebrow: 'FRANCHISE NETWORK', title: 'Đối tác nhượng quyền', singular: 'đối tác', description: 'Mỗi đối tác được tạo kèm tài khoản chủ nhượng quyền', endpoint: '/admin/partners', columns: partnerColumns, createLabel: 'Thêm đối tác', successMessage: 'Đã tạo đối tác và tài khoản chủ nhượng quyền', fields: [
    { key: 'name', label: 'Tên hiển thị', required: true }, { key: 'slug', label: 'Mã định danh', required: true, placeholder: 'doi-tac-a', lowercase: true, minLength: 2, pattern: '[a-z0-9-]+', title: 'Chỉ dùng chữ không dấu, số và dấu gạch ngang' },
    { key: 'legal_name', label: 'Tên pháp nhân' }, { key: 'tax_code', label: 'Mã số thuế' },
    { key: 'representative_name', label: 'Người đại diện / chủ tài khoản', required: true }, { key: 'email', label: 'Email đăng nhập', type: 'email', required: true },
    { key: 'phone', label: 'Điện thoại' }, { key: 'password', label: 'Mật khẩu tạm', type: 'password', required: true, optionalOnEdit: true, minLength: 12, editPlaceholder: 'Để trống nếu không đổi mật khẩu' },
    { key: 'province_code', label: 'Tỉnh / thành phố', type: 'select', endpoint: '/admin/provinces', required: true, number: true, resetFields: ['ward_code'] },
    { key: 'ward_code', label: 'Phường / xã', type: 'select', dependsOn: 'province_code', endpointFor: (provinceCode) => `/admin/provinces/${provinceCode}/wards`, required: true, number: true },
    { key: 'address_detail', label: 'Số nhà, tên đường', required: true, wide: true, placeholder: 'Ví dụ: 123 Nguyễn Huệ' },
    { key: 'status', label: 'Trạng thái', type: 'select', editOnly: true, required: true, options: [{ id: 'active', name: 'Hoạt động' }, { id: 'suspended', name: 'Tạm ngưng' }] },
  ] },
  stores: { eyebrow: 'NETWORK DIRECTORY', title: 'Cửa hàng', singular: 'cửa hàng', description: 'Danh bạ và trạng thái các điểm thuộc chuỗi', endpoint: '/admin/stores', columns: storeColumns, createLabel: 'Thêm cửa hàng', fields: [
    { key: 'tenant_id', label: 'Đối tác', type: 'select', endpoint: '/admin/partners', required: true, number: true },
    { key: 'region_id', label: 'Vùng', type: 'select', endpoint: '/admin/regions', optionLabel: (row) => `${row.code} · ${row.name}`, nullableNumber: true },
    { key: 'name', label: 'Tên cửa hàng', required: true }, { key: 'code', label: 'Mã cửa hàng', required: true },
    { key: 'slug', label: 'Mã định danh', required: true, placeholder: 'oneplay-quan-1', lowercase: true, minLength: 2, pattern: '[a-z0-9-]+', title: 'Chỉ dùng chữ không dấu, số và dấu gạch ngang' },
    { key: 'province_code', label: 'Tỉnh / thành phố', type: 'select', endpoint: '/admin/provinces', required: true, number: true, resetFields: ['ward_code'] },
    { key: 'ward_code', label: 'Phường / xã', type: 'select', dependsOn: 'province_code', endpointFor: (provinceCode) => `/admin/provinces/${provinceCode}/wards`, required: true, number: true },
    { key: 'address_detail', label: 'Số nhà, tên đường', required: true, wide: true, placeholder: 'Ví dụ: 42 Nguyễn Đình' },
    { key: 'status', label: 'Trạng thái', type: 'select', editOnly: true, required: true, options: [{ id: 'active', name: 'Hoạt động' }, { id: 'suspended', name: 'Tạm ngưng' }, { id: 'archived', name: 'Lưu trữ' }] },
    { key: 'suspension_reason', label: 'Lý do tạm ngưng', editOnly: true, wide: true, placeholder: 'Chỉ cần nhập khi chọn trạng thái Tạm ngưng' },
  ] },
  users: { eyebrow: 'ACCESS CONTROL', title: 'Người dùng và phân quyền', singular: 'tài khoản', description: 'Tài khoản nội bộ của hệ thống quản trị', endpoint: '/admin/users', columns: userColumns, createLabel: 'Thêm tài khoản', fields: [
    { key: 'full_name', label: 'Họ và tên', required: true }, { key: 'email', label: 'Email', type: 'email', required: true },
    { key: 'phone', label: 'Điện thoại' }, { key: 'password', label: 'Mật khẩu tạm', type: 'password', required: true, optionalOnEdit: true, minLength: 12, editPlaceholder: 'Để trống nếu không đổi mật khẩu' },
    { key: 'role_key', label: 'Vai trò', type: 'select', required: true, endpoint: '/admin/roles', optionValue: 'role_key', optionFilter: (role) => role.manageable },
    { key: 'tenant_id', label: 'Chủ cơ sở', type: 'select', endpoint: '/admin/partners', optionLabel: (row) => (row.representative_name ? `${row.representative_name} · ${row.name}` : row.name), nullableNumber: true, hqOnly: true, resetFields: ['store_ids'], hint: 'Bắt buộc với Chủ cơ sở, Quản lý cơ sở, Nhân viên' },
    { key: 'store_ids', label: 'Cơ sở làm việc', type: 'multiselect', endpoint: '/admin/stores', filterBy: 'tenant_id', wide: true, showIf: (form) => ['store_manager', 'staff'].includes(form.role_key), hint: 'Chỉ hiện cơ sở của Chủ cơ sở đã chọn. Đây là nơi làm việc chính để tiện theo dõi, không giới hạn quyền: tài khoản vẫn thao tác được ở mọi cơ sở của Chủ cơ sở này.' },
    { key: 'status', label: 'Trạng thái', type: 'select', editOnly: true, required: true, options: [{ id: 'active', name: 'Hoạt động' }, { id: 'disabled', name: 'Vô hiệu' }] },
  ] },
};

function ResourcePage({ type }) {
  const { setNotice, auth } = useOutletContext();
  const config = resources[type];
  const [rows, setRows] = useState(null);
  const [dialog, setDialog] = useState(null);
  const isHq = auth.user.role_key === 'super_admin';
  // Cấu trúc tổ chức (vùng, đối tác, cơ sở) do HQ quản lý; tài khoản thì theo quyền users.manage và phạm vi (backend kiểm tra lại).
  const canManage = config.fields && (isHq || (type === 'users' && auth.user.permissions?.includes('users.manage')));
  const canEditRow = (row) => isHq || row.manageable;
  const load = useCallback(() => { setRows(null); api.get(config.endpoint).then((result) => setRows(result.data || [])).catch((error) => { setRows([]); setNotice({ type: 'error', message: error.message }); }); }, [config.endpoint, setNotice]);
  useEffect(() => { load(); }, [load]);
  const saved = (mode) => {
    setDialog(null);
    setNotice({ type: 'success', message: mode === 'edit' ? `Đã cập nhật ${config.singular}` : (config.successMessage || 'Đã lưu dữ liệu quản trị') });
    load();
  };
  const removed = () => {
    setDialog(null);
    setNotice({ type: 'success', message: `Đã xóa ${config.singular}` });
    load();
  };
  const canDeleteRow = (row) => !(type === 'users' && Number(row.id) === Number(auth.user.id));
  return <main className="content-page">
    <PageHeader eyebrow={config.eyebrow} title={config.title} description={config.description} actions={<>{canManage && <button className="btn primary" onClick={() => setDialog({ type: 'form', record: null })}><Plus size={17} /> {config.createLabel}</button>}<button className="btn subtle icon-only" onClick={load} title="Làm mới"><RefreshCw size={17} /></button></>} />
    {rows === null ? <Loader /> : <section className="card table-card"><DataTable rows={rows} columns={config.columns} onEdit={canManage ? (row) => setDialog({ type: 'form', record: row }) : null} onDelete={canManage ? (row) => setDialog({ type: 'delete', record: row }) : null} canDelete={(row) => canDeleteRow(row) && canEditRow(row)} canEdit={canEditRow} /></section>}
    {dialog?.type === 'form' && <RecordDialog key={dialog.record?.id || 'create'} config={config} record={dialog.record} isHq={isHq} onClose={() => setDialog(null)} onSaved={saved} />}
    {dialog?.type === 'delete' && <DeleteDialog config={config} record={dialog.record} onClose={() => setDialog(null)} onDeleted={removed} />}
  </main>;
}

function fieldInitialValue(field, record) {
  if (field.type === 'multiselect') return String(record?.[field.key] || '').split(',').filter(Boolean).map(Number);
  if (record && field.type !== 'password') {
    const value = record[field.key];
    if (value != null) return field.type === 'date' ? String(value).slice(0, 10) : value;
  }
  return field.defaultValue ?? '';
}

function RecordDialog({ config, record, isHq, onClose, onSaved }) {
  const { setNotice } = useOutletContext();
  const isEdit = Boolean(record);
  const [form, setForm] = useState(() => Object.fromEntries(config.fields.map((field) => [field.key, fieldInitialValue(field, record)])));
  const visibleFields = config.fields.filter((field) => (isEdit ? !field.createOnly : !field.editOnly) && (isHq || !field.hqOnly) && (!field.showIf || field.showIf(form)));
  const [options, setOptions] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    // tải sẵn cả danh sách của ô đang ẩn (vd Cơ sở làm việc chỉ hiện khi chọn vai trò cấp cơ sở)
    const endpoints = [...new Set(config.fields.filter((field) => field.endpoint && !field.dependsOn && (isHq || !field.hqOnly)).map((field) => field.endpoint))];
    Promise.all(endpoints.map(async (endpoint) => [endpoint, (await api.get(endpoint)).data || []]))
      .then((entries) => setOptions((current) => ({ ...current, ...Object.fromEntries(entries) })))
      .catch((error) => setNotice({ type: 'error', message: error.message }));
  }, [config.fields, isEdit, setNotice]);
  const dependencyValues = visibleFields.filter((field) => field.dependsOn).map((field) => form[field.dependsOn] || '').join('|');
  useEffect(() => {
    let active = true;
    const dependentFields = visibleFields.filter((field) => field.dependsOn && field.endpointFor);
    for (const field of dependentFields) {
      const dependencyValue = form[field.dependsOn];
      if (!dependencyValue) {
        setOptions((current) => ({ ...current, [field.key]: [] }));
        continue;
      }
      api.get(field.endpointFor(dependencyValue))
        .then((result) => { if (active) setOptions((current) => ({ ...current, [field.key]: result.data || [] })); })
        .catch((error) => { if (active) setNotice({ type: 'error', message: error.message }); });
    }
    return () => { active = false; };
  }, [config.fields, dependencyValues, isEdit, setNotice]);
  const submit = async (event) => {
    event.preventDefault(); setSaving(true);
    try {
      const payload = {};
      for (const field of visibleFields) {
        const value = form[field.key];
        if (isEdit && field.optionalOnEdit && value === '') continue;
        if (field.type === 'multiselect') payload[field.key] = (value || []).map(Number);
        else if (field.number) payload[field.key] = Number(value || 0);
        else if (field.nullableNumber) payload[field.key] = value ? Number(value) : null;
        else payload[field.key] = value === '' && !field.required ? null : value;
      }
      if (isEdit) await api.put(`${config.endpoint}/${record.id}`, payload);
      else await api.post(config.endpoint, payload);
      onSaved(isEdit ? 'edit' : 'create');
    } catch (error) {
      const issue = error.details?.[0];
      const invalidField = issue?.path?.[0];
      const fieldLabel = config.fields.find((field) => field.key === invalidField)?.label;
      const message = fieldLabel && issue?.message ? `${fieldLabel}: ${issue.message}` : error.message;
      setNotice({ type: 'error', message });
    }
    finally { setSaving(false); }
  };
  return <div className="modal-backdrop"><div className="modal manager-modal"><button className="icon-btn modal-close" onClick={onClose}><X /></button><p className="eyebrow">{isEdit ? 'EDIT RECORD' : 'NEW RECORD'}</p><h2>{isEdit ? `Sửa ${config.singular}` : config.createLabel}</h2><form className="manager-form" onSubmit={submit}>{visibleFields.map((field) => {
    const required = isEdit && field.optionalOnEdit ? false : field.required;
    const selectOptions = ((isEdit && field.editOptions) || field.options || options[field.endpoint] || options[field.key] || [])
      .filter((option) => !field.optionFilter || field.optionFilter(option) || String(option[field.optionValue || 'id']) === String(form[field.key]))
      .map((option) => (field.optionValue ? { ...option, id: option[field.optionValue] } : option));
    if (field.type === 'multiselect') {
      const filterValue = field.filterBy ? form[field.filterBy] : '';
      const waiting = isHq && field.filterBy && !filterValue;
      const choices = (options[field.endpoint] || []).filter((row) => !filterValue || String(row[field.filterBy]) === String(filterValue));
      const picked = form[field.key] || [];
      const toggle = (id, on) => setForm({ ...form, [field.key]: on ? [...new Set([...picked, id])] : picked.filter((value) => value !== id) });
      const allOn = choices.length > 0 && choices.every((row) => picked.includes(Number(row.id)));
      return <div className={`check-field ${field.wide ? 'wide' : ''}`} key={field.key}><span>{field.label}</span>
        {waiting ? <small className="field-hint">Chọn Chủ cơ sở trước.</small> : <div className="check-list">
          <label className="check-all"><input type="checkbox" checked={allOn} onChange={(event) => setForm({ ...form, [field.key]: event.target.checked ? choices.map((row) => Number(row.id)) : [] })} /> Tất cả cơ sở</label>
          {choices.map((row) => <label key={row.id}><input type="checkbox" checked={picked.includes(Number(row.id))} onChange={(event) => toggle(Number(row.id), event.target.checked)} /> {row.name}</label>)}
          {!choices.length && <small className="field-hint">Chủ cơ sở này chưa có cơ sở nào.</small>}
        </div>}
        {field.hint && <small className="field-hint">{field.hint}</small>}</div>;
    }
    return <label className={field.wide ? 'wide' : ''} key={field.key}>{field.label}{field.type === 'select' ? <select required={required} disabled={Boolean(field.dependsOn && !form[field.dependsOn])} value={form[field.key]} onChange={(event) => { const next = { ...form, [field.key]: event.target.value }; for (const resetKey of field.resetFields || []) next[resetKey] = Array.isArray(form[resetKey]) ? [] : ''; setForm(next); }}><option value="">{field.dependsOn && !form[field.dependsOn] ? 'Chọn tỉnh/thành trước...' : 'Chọn...'}</option>{selectOptions.map((option) => <option key={option.id} value={option.id}>{field.optionLabel ? field.optionLabel(option) : option.name}</option>)}</select> : <input type={field.type || 'text'} step={field.type === 'number' ? 'any' : undefined} required={required} minLength={field.minLength} pattern={field.pattern} title={field.title} autoCapitalize={field.lowercase ? 'none' : undefined} spellCheck={field.lowercase ? false : undefined} placeholder={(isEdit && field.editPlaceholder) || field.placeholder || ''} value={form[field.key]} onChange={(event) => setForm({ ...form, [field.key]: field.lowercase ? event.target.value.toLowerCase() : event.target.value })} />}{field.hint && <small className="field-hint">{field.hint}</small>}</label>;
  })}<div className="form-actions wide"><button type="button" className="btn subtle" onClick={onClose}>Hủy</button><button className="btn primary" disabled={saving}>{saving ? <RefreshCw className="spin" size={17} /> : <Save size={17} />} {isEdit ? 'Lưu thay đổi' : 'Lưu'}</button></div></form></div></div>;
}

function DeleteDialog({ config, record, onClose, onDeleted }) {
  const { setNotice } = useOutletContext();
  const [deleting, setDeleting] = useState(false);
  const remove = async () => {
    setDeleting(true);
    try {
      await api.delete(`${config.endpoint}/${record.id}`);
      onDeleted();
    } catch (error) {
      setNotice({ type: 'error', message: error.message });
      setDeleting(false);
    }
  };
  return <div className="modal-backdrop"><div className="modal confirm-modal"><button className="icon-btn modal-close" onClick={onClose}><X /></button><div className="danger-icon"><AlertTriangle /></div><p className="eyebrow">DELETE RECORD</p><h2>Xóa {config.singular}?</h2><p>Dữ liệu sẽ bị xóa khỏi hệ thống. Nếu bản ghi đang được sử dụng, hệ thống sẽ tự chặn thao tác này.</p><div className="form-actions"><button className="btn subtle" onClick={onClose}>Hủy</button><button className="btn danger" onClick={remove} disabled={deleting}>{deleting ? <RefreshCw className="spin" size={17} /> : <Trash2 size={17} />} Xóa</button></div></div></div>;
}

function DataTable({ rows, columns, compact = false, onEdit = null, onDelete = null, canDelete = () => true, canEdit = () => true }) {
  const keys = Object.keys(columns);
  const hasActions = Boolean(onEdit || onDelete);
  if (!rows?.length) return <Empty />;
  return <div className={`table-wrap ${compact ? 'compact' : ''}`}><table><thead><tr>{keys.map((key) => <th key={key}>{columns[key]}</th>)}{hasActions && <th className="actions-column">Thao tác</th>}</tr></thead><tbody>{rows.map((row, index) => <tr key={row.id || index}>{keys.map((key) => <td key={key}>{formatValue(row[key], key)}</td>)}{hasActions && <td className="actions-column"><div className="row-actions">{onEdit && <button className="table-action edit" disabled={!canEdit(row)} onClick={() => onEdit(row)} title={canEdit(row) ? 'Sửa' : 'Ngoài phạm vi quản lý của bạn'}><Pencil size={15} /></button>}{onDelete && <button className="table-action delete" disabled={!canDelete(row)} onClick={() => onDelete(row)} title={canDelete(row) ? 'Xóa' : 'Không thể xóa bản ghi này'}><Trash2 size={15} /></button>}</div></td>}</tr>)}</tbody></table></div>;
}

function formatValue(value, key) {
  if (value == null || value === '') return <span className="muted">—</span>;
  if (key === 'status') return <span className={`status-pill ${value}`}>{statusLabel(value)}</span>;
  if (key === 'active') return <span className={`status-pill ${value ? 'active' : 'disabled'}`}>{value ? 'Đang hiệu lực' : 'Ngừng hiệu lực'}</span>;
  if (['amount', 'fixed_amount', 'minimum_amount', 'revenue_base', 'outstanding_amount'].includes(key)) return money.format(Number(value));
  if (key.endsWith('_at')) return dateTime.format(new Date(value));
  if (key.endsWith('_on') || key.startsWith('period_')) return date.format(new Date(value));
  if (key === 'fee_type') return ({ percent: 'Theo tỷ lệ', fixed: 'Cố định', hybrid: 'Kết hợp' })[value] || value;
  return String(value);
}

function statusLabel(value) {
  return ({ active: 'Hoạt động', suspended: 'Tạm ngưng', archived: 'Lưu trữ', disabled: 'Vô hiệu', draft: 'Bản nháp', issued: 'Đã phát hành', paid: 'Đã thanh toán', overdue: 'Quá hạn' })[value] || value;
}

function SettingsPage() {
  const { setNotice } = useOutletContext();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get('/admin/settings').then((data) => setForm(data.settings.general)).catch((error) => setNotice({ type: 'error', message: error.message })); }, [setNotice]);
  if (!form) return <Loader />;
  const save = async () => { setSaving(true); try { const data = await api.put('/admin/settings/general', form); setForm(data.settings); setNotice({ type: 'success', message: 'Đã lưu cấu hình hệ thống' }); } catch (error) { setNotice({ type: 'error', message: error.message }); } finally { setSaving(false); } };
  return <main className="content-page"><PageHeader eyebrow="SYSTEM SETTINGS" title="Cấu hình chung" description="Thông tin nhận diện và thiết lập mặc định của OnePlay Managers" actions={<button className="btn primary" onClick={save} disabled={saving}><Save size={17} /> Lưu thay đổi</button>} /><section className="card settings-panel"><label>Tên hệ thống<input value={form.system_name || ''} onChange={(event) => setForm({ ...form, system_name: event.target.value })} /></label><label>Email hỗ trợ<input type="email" value={form.support_email || ''} onChange={(event) => setForm({ ...form, support_email: event.target.value })} /></label><label>Múi giờ mặc định<input value={form.default_timezone || ''} onChange={(event) => setForm({ ...form, default_timezone: event.target.value })} /></label><div className="settings-note"><Globe2 /><div><strong>Phạm vi hệ thống</strong><span>Cấu hình này áp dụng cho màn hình quản trị, không can thiệp hệ thống vận hành tại cửa hàng.</span></div></div></section></main>;
}

function Empty() { return <div className="empty-state"><div><Landmark /></div><h3>Chưa có dữ liệu</h3><p>Dữ liệu quản trị sẽ xuất hiện tại đây sau khi được tạo.</p></div>; }
function Loader() { return <div className="loader"><RefreshCw className="spin" /> Đang đồng bộ dữ liệu...</div>; }

export default function App() {
  const [theme, setTheme] = useState(() => localStorage.getItem('oneplay_theme') === 'light' ? 'light' : 'dark');
  const [auth, setAuth] = useState(null);
  const handoffCode = BOOKING_HOST || SCHEDULE_HOST ? new URLSearchParams(window.location.hash.slice(1)).get('handoff') : null;
  const [loading, setLoading] = useState(Boolean(session.accessToken || handoffCode));
  const [notice, setNotice] = useState(null);
  const navigate = useNavigate();
  const bootstrap = useCallback(async () => { setLoading(true); try { setAuth(await api.get('/auth/me')); } catch { session.clear(); setAuth(null); } finally { setLoading(false); } }, []);
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme;
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', theme === 'light' ? '#f4f7fb' : '#090b17');
    localStorage.setItem('oneplay_theme', theme);
  }, [theme]);
  useEffect(() => {
    if (!handoffCode) { if (session.accessToken) bootstrap(); return; }
    if (handoffStarted) return; // mã dùng một lần: không đổi hai lần
    handoffStarted = true;
    // xoá mã khỏi thanh địa chỉ ngay (dùng một lần), rồi đổi lấy phiên
    window.history.replaceState(null, '', window.location.pathname);
    api.redeemHandoff(handoffCode)
      .then(() => bootstrap())
      .catch((error) => { setNotice({ type: 'error', message: error.message }); if (session.accessToken) bootstrap(); else setLoading(false); });
  }, [bootstrap]);
  const logout = async () => { await api.logout(); setAuth(null); setNotice(null); navigate('/login'); };
  if (loading) return <div className="splash"><Logo /><Loader /></div>;
  if (auth?.user.role_key === 'staff' && SCHEDULE_HOST) {
    return <main className="login-shell"><section className="login-card">
      <Logo /><p className="eyebrow">ONEPLAY · LỊCH LÀM</p><h2>Nhân viên dùng OnePlay Station</h2>
      <p>Trang này dành cho Quản lý cơ sở và Chủ nhượng quyền xếp lịch. Nhân viên pick ca, xem lịch đã chốt và xin đổi ca trong OnePlay Station → Lịch làm.</p>
      <a className="btn primary wide" href="https://station.oneplayplatform.com"><CalendarDays size={18} /> Mở OnePlay Station</a>
      <button className="btn subtle wide" onClick={logout}><LogOut size={17} /> Đăng xuất</button>
    </section></main>;
  }
  if (auth?.user.role_key === 'staff' && !BOOKING_HOST) {
    return <main className="login-shell"><section className="login-card">
      <Logo /><p className="eyebrow">ONEPLAY MANAGERS</p><h2>Nhân viên dùng trang Booking</h2>
      <p>Tài khoản Nhân viên không vào trang quản trị. Mở trang Booking để đặt lịch máy và đổi quà.</p>
      <a className="btn primary wide" href="https://booking.oneplayplatform.com"><BookOpenCheck size={18} /> Mở trang Booking</a>
      <button className="btn subtle wide" onClick={logout}><LogOut size={17} /> Đăng xuất</button>
    </section></main>;
  }
  if (SCHEDULE_HOST) return <Routes>
    <Route path="/login" element={auth ? <Navigate to="/" replace /> : <LoginPage variant="schedule" onLogin={bootstrap} notice={notice} setNotice={setNotice} theme={theme} setTheme={setTheme} />} />
    <Route path="/" element={auth ? <ScheduleLayout auth={auth} onLogout={logout} notice={notice} setNotice={setNotice} theme={theme} setTheme={setTheme} /> : <Navigate to="/login" replace />}>
      <Route index element={<SchedulePage />} />
    </Route>
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>;
  if (BOOKING_HOST) return <Routes>
    <Route path="/login" element={auth ? <Navigate to="/" replace /> : <LoginPage variant="booking" onLogin={bootstrap} notice={notice} setNotice={setNotice} theme={theme} setTheme={setTheme} />} />
    <Route path="/" element={auth ? <BookingLayout auth={auth} onLogout={logout} notice={notice} setNotice={setNotice} theme={theme} setTheme={setTheme} /> : <Navigate to="/login" replace />}>
      <Route index element={<BookingPage />} />
      <Route path="doi-qua" element={<RedemptionsPage />} />
    </Route>
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>;
  return <Routes>
    <Route path="/login" element={auth ? <Navigate to="/app/dashboard" replace /> : <LoginPage onLogin={bootstrap} notice={notice} setNotice={setNotice} theme={theme} setTheme={setTheme} />} />
    <Route path="/app" element={auth ? <AppLayout auth={auth} onLogout={logout} notice={notice} setNotice={setNotice} theme={theme} setTheme={setTheme} /> : <Navigate to="/login" replace />}>
      <Route index element={<Navigate to="dashboard" replace />} />
      <Route path="dashboard" element={<OperationsDashboard />} />
      <Route path="booking" element={<BookingPage />} />
      <Route path="schedule" element={<SchedulePage />} />
      <Route path="customers" element={<CustomersPage />} />
      <Route path="points" element={<PointLedgerPage />} />
      <Route path="point-rules" element={<PointRulesPage />} />
      <Route path="rewards" element={<RewardsPage />} />
      <Route path="inventory" element={<InventoryPage />} />
      <Route path="redemptions" element={<RedemptionsPage />} />
      <Route path="cash-shifts" element={<CashShiftsPage />} />
      <Route path="reports" element={<ReportsPage />} />
      <Route path="regions" element={<ResourcePage type="regions" />} />
      <Route path="partners" element={<ResourcePage type="partners" />} />
      <Route path="stores" element={<ResourcePage type="stores" />} />
      <Route path="users" element={<ResourcePage type="users" />} />
      <Route path="access" element={<AccessPage />} />
      <Route path="audit" element={<AuditPage />} />
      <Route path="settings" element={<SettingsPage />} />
    </Route>
    <Route path="*" element={<Navigate to={auth ? '/app/dashboard' : '/login'} replace />} />
  </Routes>;
}
